const HTML_ESCAPE_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/**
 * Escape a string for safe interpolation into an HTML string (e.g. Leaflet
 * tooltip content, which is set via innerHTML — see DivOverlay._updateContent
 * in node_modules/leaflet). Any player-controlled text rendered into another
 * player's browser (ship name, company name) MUST go through this first;
 * otherwise a ship/company name like `<img src=x onerror=...>` executes
 * arbitrary JS for anyone who sees that marker.
 */
export function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, ch => HTML_ESCAPE_MAP[ch]);
}

// One shared formatter: Number#toLocaleString builds a new Intl formatter on
// every call, which dominated the Market table's ~18k-cell rebuild.
const MONEY_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** Format a number as currency: $1,234,567 */
export function formatMoney(amount) {
  const n = Math.round(amount);
  const sign = n < 0 ? '-' : '';
  return sign + '$' + MONEY_FORMAT.format(Math.abs(n));
}

/** Format a Date to "Jan 01, 2024 14:30" */
export function formatDate(date) {
  if (!(date instanceof Date)) date = new Date(date);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const m  = months[date.getMonth()];
  const d  = String(date.getDate()).padStart(2, '0');
  const y  = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${m} ${d}, ${y} ${hh}:${mm}`;
}

/** Format days into "X days Y hrs" */
export function formatDuration(days) {
  const d = Math.floor(days);
  const h = Math.round((days - d) * 24);
  if (d === 0) return h + ' hrs';
  if (h === 0) return d + ' day' + (d !== 1 ? 's' : '');
  return d + ' day' + (d !== 1 ? 's' : '') + ' ' + h + ' hrs';
}

/** Compact large numbers: 1234567 -> "1.23M" */
export function formatCompact(n) {
  if (Math.abs(n) >= 1e9) return (n / 1e9).toFixed(2) + 'B';
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(2) + 'M';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return String(Math.round(n));
}

/** Percentage with one decimal */
export function formatPercent(n) {
  return (n * 100).toFixed(1) + '%';
}
